import React, { useEffect, useState } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { HC } from "./theme";
import { Card, SectionHead, Pill, Btn, Table, Field, Input, Select, Avatar } from "./ui";
import { toast } from "sonner";
import { LogIn, LogOut, Pencil, Plus, History } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

const STATUSES = ["present", "late", "short_shift", "late_short", "no_schedule", "absent", "on_leave", "half_day"];
const today = () => new Date().toISOString().slice(0, 10);

export default function Attendance() {
  const { locationId } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const [date, setDate] = useState(today());
  const [status, setStatus] = useState(params.get("status") || "");
  const [employeeId, setEmployeeId] = useState(params.get("employee") || "");
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [record, setRecord] = useState(null);
  const [correct, setCorrect] = useState(null);
  const [confirmOut, setConfirmOut] = useState(null);

  const load = () => api.get("/hr/attendance", {
    params: {
      date: employeeId ? undefined : date, employee_id: employeeId || undefined,
      location_id: locationId || undefined, status: status || undefined,
    },
  }).then(setRows).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => { api.get("/hr/employees", { params: { location_id: locationId || undefined } }).then(setEmployees); /* eslint-disable-next-line */ }, [locationId]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, status, locationId, employeeId]);
  useEffect(() => {
    if (params.get("record")) { setRecord({ employee_id: "", date: today(), clock_in: "07:00", clock_out: "", status: "present", source: "manual" }); setParams({}); }
    /* eslint-disable-next-line */
  }, [params]);

  const summary = STATUSES.map((s) => ({ s, n: rows.filter((r) => r.status === s).length }));

  const doClock = async (kind, employee_id) => {
    try {
      await api.post(`/hr/attendance/${kind}`, { employee_id, date });
      toast.success(kind === "clock-in" ? "Clocked in" : "Clocked out"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveRecord = async () => {
    try {
      await api.post("/hr/attendance", record);
      toast.success("Attendance recorded"); setRecord(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveCorrection = async () => {
    if (!correct.reason?.trim()) return toast.error("A correction reason is required");
    try {
      await api.put(`/hr/attendance/${correct.id}`, correct);
      toast.success("Attendance corrected — audit trail updated"); setCorrect(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="hr-attendance-page">
      <Card>
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          <Field label="Date" className="lg:w-52"><Input data-testid="att-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
          <Field label="Status" className="lg:w-48">
            <Select data-testid="att-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
            </Select>
          </Field>
          <div className="flex-1 flex flex-wrap gap-2 items-center">
            {employeeId && (
              <button onClick={() => { setEmployeeId(""); setParams({}); }} data-testid="att-clear-employee"
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full"
                style={{ background: HC.purpleTint, color: HC.purple }}>
                Showing one employee's history · clear
              </button>
            )}
            {summary.map(({ s, n }) => <Pill key={s} status={s} text={`${s.replace(/_/g, " ")}: ${n}`} testid={`att-sum-${s}`} />)}
          </div>
          <Btn onClick={() => setRecord({ employee_id: "", date, clock_in: "07:00", clock_out: "", status: "present", source: "manual" })}
            data-testid="hr-record-attendance"><Plus size={15} /> Record Attendance</Btn>
        </div>
      </Card>

      <Card>
        <SectionHead title={`Attendance — ${date}`} sub="Manual corrections require a reason and are fully audited" />
        <Table
          testid="hr-attendance-table" rows={rows} empty="No attendance records for this date"
          columns={[
            { key: "employee_name", label: "EMPLOYEE", render: (r) => (
                <span className="flex items-center gap-2.5"><Avatar name={r.employee_name} />
                  <span><span className="block font-semibold">{r.employee_name}</span>
                    <span className="block text-[11px]" style={{ color: HC.soft }}>{r.employee_no}</span></span></span>) },
            { key: "department_name", label: "DEPARTMENT" },
            { key: "location_name", label: "LOCATION" },
            { key: "clock_in", label: "IN", render: (r) => <span className="tabular-nums">{r.clock_in || "—"}</span> },
            { key: "clock_out", label: "OUT", render: (r) => <span className="tabular-nums">{r.clock_out || "—"}</span> },
            { key: "late_minutes", label: "LATE (MIN)", align: "right", render: (r) => r.late_minutes || 0 },
            { key: "overtime_minutes", label: "OT (MIN)", align: "right", render: (r) => r.overtime_minutes || 0 },
            { key: "source", label: "SOURCE", render: (r) => (
                <span className="text-[11.5px]" style={{ color: HC.soft }}>
                  {r.source}{r.kiosk_id ? ` · ${r.kiosk_id}` : ""}
                </span>) },
            { key: "status", label: "STATUS", render: (r) => (
                <span className="flex items-center gap-1.5">
                  <Pill status={r.status} />
                  {!!r.corrections?.length && <History size={13} style={{ color: HC.soft }} title={`${r.corrections.length} correction(s)`} />}
                </span>) },
            { key: "actions", label: "", align: "right", render: (r) => (
                <span className="flex items-center gap-1.5 justify-end">
                  {!r.clock_in && <Btn variant="outline" className="h-8 !px-2.5" data-testid={`clockin-${r.employee_id}`}
                    onClick={() => doClock("clock-in", r.employee_id)}><LogIn size={14} /></Btn>}
                  {r.clock_in && !r.clock_out && <Btn variant="outline" className="h-8 !px-2.5" data-testid={`clockout-${r.employee_id}`}
                    onClick={() => doClock("clock-out", r.employee_id)}><LogOut size={14} /></Btn>}
                  <Btn variant="outline" className="h-8 !px-2.5" data-testid={`correct-${r.id}`}
                    onClick={() => setCorrect({ ...r, reason: "" })}><Pencil size={14} /></Btn>
                </span>) },
          ]}
        />
      </Card>

      <Dialog open={!!record} onOpenChange={(o) => !o && setRecord(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record attendance</DialogTitle>
            <DialogDescription>Manual entries are tagged with source and user for audit.</DialogDescription>
          </DialogHeader>
          {record && (
            <div className="space-y-3">
              <Field label="Employee">
                <Select data-testid="rec-employee" value={record.employee_id} onChange={(e) => setRecord({ ...record, employee_id: e.target.value })}>
                  <option value="">Select employee</option>
                  {employees.map((e) => <option key={e.id} value={e.id}>{e.name} — {e.department_name}</option>)}
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date"><Input type="date" value={record.date} onChange={(e) => setRecord({ ...record, date: e.target.value })} /></Field>
                <Field label="Status">
                  <Select value={record.status} onChange={(e) => setRecord({ ...record, status: e.target.value })}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </Select>
                </Field>
                <Field label="Clock in"><Input data-testid="rec-in" type="time" value={record.clock_in || ""} onChange={(e) => setRecord({ ...record, clock_in: e.target.value })} /></Field>
                <Field label="Clock out"><Input data-testid="rec-out" type="time" value={record.clock_out || ""} onChange={(e) => setRecord({ ...record, clock_out: e.target.value })} /></Field>
              </div>
              <Field label="Notes"><Input value={record.notes || ""} onChange={(e) => setRecord({ ...record, notes: e.target.value })} /></Field>
            </div>
          )}
          <DialogFooter>
            <Btn variant="outline" onClick={() => setRecord(null)}>Cancel</Btn>
            <Btn onClick={saveRecord} data-testid="rec-save">Save record</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!correct} onOpenChange={(o) => !o && setCorrect(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Correct attendance</DialogTitle>
            <DialogDescription>{correct?.employee_name} · {correct?.date} — a reason is mandatory.</DialogDescription>
          </DialogHeader>
          {correct && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Clock in"><Input data-testid="cor-in" type="time" value={correct.clock_in || ""} onChange={(e) => setCorrect({ ...correct, clock_in: e.target.value })} /></Field>
                <Field label="Clock out"><Input data-testid="cor-out" type="time" value={correct.clock_out || ""} onChange={(e) => setCorrect({ ...correct, clock_out: e.target.value })} /></Field>
                <Field label="Status">
                  <Select data-testid="cor-status" value={correct.status} onChange={(e) => setCorrect({ ...correct, status: e.target.value })}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                  </Select>
                </Field>
                <Field label="Break (min)"><Input type="number" value={correct.break_minutes || 0} onChange={(e) => setCorrect({ ...correct, break_minutes: Number(e.target.value) })} /></Field>
              </div>
              <Field label="Reason (required)">
                <Input data-testid="cor-reason" value={correct.reason} onChange={(e) => setCorrect({ ...correct, reason: e.target.value })}
                  placeholder="e.g. Clock-in device offline; verified by supervisor" />
              </Field>
              {!!correct.corrections?.length && (
                <div className="rounded-xl border p-3" style={{ borderColor: HC.border }}>
                  <div className="text-[10px] font-bold tracking-[0.14em] mb-2" style={{ color: HC.soft }}>CORRECTION HISTORY</div>
                  {correct.corrections.map((c, i) => (
                    <div key={i} className="text-[11.5px] mb-1" style={{ color: HC.muted }}>
                      {new Date(c.at).toLocaleString("en-NG")} · {c.by} — {c.reason}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Btn variant="outline" onClick={() => setCorrect(null)}>Cancel</Btn>
            <Btn onClick={saveCorrection} data-testid="cor-save">Save correction</Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!confirmOut} onOpenChange={(o) => !o && setConfirmOut(null)}>
        <DialogContent data-testid="att-clockout-confirm">
          <DialogHeader>
            <DialogTitle>Clock out {confirmOut?.employee_name}?</DialogTitle>
            <DialogDescription>
              This writes a clock-out time of {new Date().toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" })} on
              {" "}{confirmOut?.date} and feeds payroll. It cannot be undone — only corrected with a reason.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Btn variant="outline" onClick={() => setConfirmOut(null)} data-testid="att-clockout-cancel">Cancel</Btn>
            <Btn data-testid="att-clockout-confirm-btn"
              onClick={async () => { const r = confirmOut; setConfirmOut(null); await doClock("clock-out", r.employee_id); }}>
              Yes, clock out
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!confirmOut} onOpenChange={(o) => !o && setConfirmOut(null)}>
        <DialogContent data-testid="att-clockout-confirm">
          <DialogHeader>
            <DialogTitle>Clock out {confirmOut?.employee_name}?</DialogTitle>
            <DialogDescription>
              This writes a clock-out time on {confirmOut?.date} and feeds payroll. It cannot be
              undone — only corrected with a reason.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Btn variant="outline" onClick={() => setConfirmOut(null)} data-testid="att-clockout-cancel">Cancel</Btn>
            <Btn data-testid="att-clockout-confirm-btn"
              onClick={async () => { const r = confirmOut; setConfirmOut(null); await doClock("clock-out", r.employee_id); }}>
              Yes, clock out
            </Btn>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
